import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import { FiArrowUpRight, FiAward } from 'react-icons/fi';
import SectionHead from '../ui/SectionHead';
import Reveal from '../ui/Reveal';
import useMediaQuery from '../../hooks/useMediaQuery';
import { featuredWork, moreWork } from '../../data/profile';
import './Work.css';

function GeneratedCover({ glyph, title }) {
  return (
    <div className="gcover" aria-label={`${title} cover`}>
      <div className="gcover__grid" />
      <div className="gcover__glow" />
      <span className="gcover__glyph">{glyph}</span>
      <div className="gcover__ui">
        <div className="gcover__line gcover__line--w1" />
        <div className="gcover__line gcover__line--w2" />
        <div className="gcover__line gcover__line--w3" />
        <div className="gcover__chip">RAG &middot; UAE LAW &middot; MEETINGS</div>
      </div>
    </div>
  );
}

function Cover({ cover, title }) {
  if (cover.type === 'generated') return <GeneratedCover glyph={cover.glyph} title={title} />;
  return (
    <img
      src={cover.src}
      alt={cover.alt}
      loading="lazy"
      className={cover.fit === 'contain' ? 'is-contain' : ''}
      style={cover.bg ? { background: cover.bg } : undefined}
    />
  );
}

function StackCard({ project, i, total, progress, stacked }) {
  const targetScale = 1 - (total - i) * 0.035;
  const range = [i / total, 1];
  const scale = useTransform(progress, range, [1, targetScale]);
  const dim = useTransform(progress, range, [0, 0.55]);
  const style = stacked ? { scale, top: `calc(90px + ${i * 22}px)` } : {};

  return (
    <div className={`work__slot ${stacked ? 'work__slot--sticky' : ''}`}>
      <motion.article className="work__card card" style={style}>
        {stacked && <motion.div className="work__dim" style={{ opacity: dim }} aria-hidden />}
        <div className="work__info">
          <div className="work__meta">
            <span className="mono accent">{project.index}</span>
            <span className="mono">{project.year}</span>
            <span className="mono">{project.org}</span>
          </div>
          <h3 className="h2 work__title">{project.title}</h3>
          <p className="work__kicker">{project.kicker}</p>
          <p className="body work__desc">{project.description}</p>
          {project.award && (
            <p className="work__award">
              <FiAward /> {project.award}
            </p>
          )}
          <ul className="work__tags">
            {project.tags.map((t) => (
              <li key={t} className="pill">{t}</li>
            ))}
          </ul>
          <a href={project.href} target="_blank" rel="noreferrer" className="btn btn--ghost work__cta">
            {project.cta} <span className="btn__icon"><FiArrowUpRight /></span>
          </a>
        </div>
        <div className="work__cover">
          <Cover cover={project.cover} title={project.title} />
        </div>
      </motion.article>
    </div>
  );
}

function MoreRow({ item }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 26, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 260, damping: 26, mass: 0.4 });

  const onMove = (e) => {
    if (reduce) return;
    const r = ref.current.getBoundingClientRect();
    x.set(e.clientX - r.left);
    y.set(e.clientY - r.top);
  };

  return (
    <li className="more__row" ref={ref} onMouseMove={onMove}>
      <a href={item.href} target="_blank" rel="noreferrer" className="more__link">
        <div className="more__main">
          <h4 className="more__title">{item.title}</h4>
          <p className="small more__desc">{item.description}</p>
        </div>
        <ul className="more__tags">
          {item.tags.map((t) => (
            <li key={t} className="mono">{t}</li>
          ))}
        </ul>
        <span className="more__arrow"><FiArrowUpRight /></span>
      </a>
      {item.secondary && (
        <a href={item.secondary.href} target="_blank" rel="noreferrer" className="more__secondary link link--quiet">
          {item.secondary.label} <FiArrowUpRight size={12} />
        </a>
      )}
      <motion.div className={`more__thumb ${item.image ? '' : 'more__thumb--glyph'}`} style={{ x: sx, y: sy }} aria-hidden>
        {item.image ? <img src={item.image} alt="" loading="lazy" /> : <span>{item.glyph}</span>}
      </motion.div>
    </li>
  );
}

export default function Work() {
  const ref = useRef(null);
  const stacked = useMediaQuery('(min-width: 900px) and (prefers-reduced-motion: no-preference)');
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

  return (
    <section className="section work" id="work">
      <div className="container">
        <SectionHead
          index="02"
          label="Selected work"
          title={[{ text: 'Things I’ve built that' }, { text: 'shipped,', em: true }, { text: 'won, or got published.' }]}
          aside={<p className="small">Four projects that best explain how I think. Scroll through them.</p>}
        />
      </div>

      <div className={`container work__stack ${stacked ? 'is-stacked' : ''}`} ref={ref}>
        {featuredWork.map((p, i) => (
          <StackCard key={p.id} project={p} i={i} total={featuredWork.length} progress={scrollYProgress} stacked={stacked} />
        ))}
      </div>

      <div className="container more">
        <Reveal className="more__head">
          <span className="mono">Also built</span>
          <span className="rule" />
        </Reveal>
        <ul className="more__list">
          {moreWork.map((m) => (
            <MoreRow key={m.title} item={m} />
          ))}
        </ul>
      </div>
    </section>
  );
}
